import { useCallback, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import { Navigation } from '@code-burrow/design-system';
import { AccountMenu, AuthGate, ListsOverview, NotificationService, NotificationsPage, PodsPage, PostAddPrompt, SearchOverlay, SearchResultRow, SettingsPage, UserViewProvider, UsersProvider, isCombinedView, useCurrentUser, useNotifications, useUserView, useUsers } from '@code-burrow/tracker-core';

import { BoardgameCollectionView } from './adapters/boardgameCollectionAdapter';
import ListDetailPage from './components/ListDetailPage';
import BackendService from './services/BackendService';
import logo from './assets/logo.png';
import TrendingCarousel from './components/TrendingCarousel';
import TrendingSignInBackground from './components/TrendingSignInBackground';

function AppContent() {
    const { getToken } = useAuth();
    const backendService = useMemo(() => new BackendService(getToken), [getToken]);
    const pocketbaseURL = import.meta.env.VITE_POCKETBASE_URL;
    const { users } = useUsers();
    const { activeView } = useUserView();
    const currentUser = useCurrentUser();

    // Notifications
    const notifServiceRef = useRef(null);
    if (!notifServiceRef.current && currentUser?.id) {
        notifServiceRef.current = new NotificationService(currentUser.id, getToken);
    }
    const { notifications, unreadCount, markAllRead, loading: notifLoading } = useNotifications(notifServiceRef.current, 'boardgames');
    const navigate = useNavigate();
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchInitialQuery, setSearchInitialQuery] = useState('');
    const [searchCameraOnOpen, setSearchCameraOnOpen] = useState(false);
    const [collectionItems, setCollectionItems] = useState([]);
    const [collectionLoaded, setCollectionLoaded] = useState(false);
    const [trendingDismissed, setTrendingDismissed] = useState(() => {
        try { return localStorage.getItem('boardgame-tracker:trending-dismissed') === 'true'; } catch { return false; }
    });
    const [searchSelection, setSearchSelection] = useState(null);

    const handleItemsLoaded = useCallback((items) => {
        setCollectionItems(items);
        setCollectionLoaded(true);
    }, []);

    const handleSearchSelect = (item) => {
        setSearchSelection({ ...item, _ts: Date.now() });
        navigate('/');
    };

    // External BGG search for the SearchOverlay
    const handleExternalSearch = useCallback(async (query) => {
        try {
            const result = await backendService.searchBoardgames(query);
            if (result.success) {
                return { results: result.items.slice(0, 20) };
            }
            return { results: [], error: result.error };
        } catch (err) {
            return { results: [], error: err.message };
        }
    }, [backendService]);

    // Add a BGG game to the collection directly from search overlay
    const handleExternalAction = useCallback(async (item) => {
        // Fetch full details from BGG
        const detailResult = await backendService.getBoardgameDetails(item.bggId);
        if (!detailResult.success) throw new Error(detailResult.error);

        const details = detailResult.details;
        const addResult = await backendService.addBoardgame({
            bggId: String(details.bggId),
            title: details.title,
            yearPublished: details.yearPublished || null,
            description: details.description || '',
            minPlayers: details.minPlayers || null,
            maxPlayers: details.maxPlayers || null,
            playingTime: details.playingTime || null,
            minPlayTime: details.minPlayTime || null,
            maxPlayTime: details.maxPlayTime || null,
            minAge: details.minAge || null,
            designer: details.designers || [],
            publisher: details.publishers || [],
            categories: details.categories || [],
            mechanics: details.mechanics || [],
            imageURL: details.imageURL || '',
            bggRating: details.bggRating || null,
            bggWeight: details.bggWeight || null,
        });

        if (!addResult.success) throw new Error(addResult.error);

        // Always add for current user only — co-logging handled by TogetherPrompt/PodMemberPicker
        const targetUser = isCombinedView(activeView) ? currentUser?.name : activeView;
        if (targetUser) {
            await backendService.upsertUserBoardgame(targetUser, addResult.boardgame.id, {
                playedDate: new Date().toISOString().split('T')[0],
            });
        }
        return addResult.boardgame || null;
    }, [backendService, users, activeView]);

    const navLinks = [
        {
            path: '/',
            label: 'Games',
            icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" /></svg>,
        },
        {
            path: '/lists',
            label: 'Lists',
            icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" /></svg>,
        },
        {
            path: '/pods',
            label: 'Pods',
            icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" /></svg>,
        },
    ];

    return (
        <div className="min-h-screen bg-bg-primary pb-20 lg:pb-0">
            <Navigation
                onSearchOpen={() => setSearchOpen(true)}
                onScanOpen={() => { setSearchCameraOnOpen(true); setSearchOpen(true); }}
                accentBorder
            >
                <Navigation.Brand
                    appName="Boardgame Tracker"
                    logo={<img src={logo} alt="Boardgame Tracker" className="h-8 w-8 object-contain" />}
                />
                <div className="flex items-center gap-4">
                    <Navigation.Links links={navLinks} />
                    <AccountMenu
                        unreadCount={unreadCount}
                        settingsLinks={[
                            { path: '/library', label: 'Library', icon: <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0l-3-3m3 3l3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" /></svg> },
                            { path: '/notifications', label: 'Notifications', badge: unreadCount, icon: <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" /></svg> },
                            { path: '/settings', label: 'Settings', icon: <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg> },
                        ]}
                    />
                    <button
                        onClick={() => setSearchOpen(true)}
                        className="hidden lg:flex items-center gap-2 h-9 px-3 rounded-md bg-bg-primary border border-border text-text-tertiary text-sm hover:text-text-primary hover:border-border-strong transition-colors duration-150"
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                        </svg>
                        <span>Search</span>
                        <kbd className="ml-1 px-1.5 py-0.5 rounded bg-surface border border-border-subtle text-[10px] font-mono text-text-tertiary">{navigator.platform?.includes('Mac') ? '\u2318K' : 'Ctrl+K'}</kbd>
                    </button>
                </div>
            </Navigation>
            <Navigation.TabBar links={navLinks} onSearchOpen={() => setSearchOpen(true)} onScanOpen={() => { setSearchCameraOnOpen(true); setSearchOpen(true); }} />

            <Routes>
                <Route
                    path="/"
                    element={
                        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4 pb-8">
                            <BoardgameCollectionView
                                backendService={backendService}
                                pocketbaseURL={pocketbaseURL}
                                onItemsLoaded={handleItemsLoaded}
                                externalSelect={searchSelection}
                                pageMode="thisYear"
                                onSearchOpen={() => setSearchOpen(true)}
                            />
                            {collectionLoaded && collectionItems.length === 0 && !trendingDismissed && (
                                <TrendingCarousel
                                    onItemClick={(item) => {
                                        setSearchInitialQuery(item.title);
                                        setSearchOpen(true);
                                    }}
                                    onDismiss={() => {
                                        setTrendingDismissed(true);
                                        try { localStorage.setItem('boardgame-tracker:trending-dismissed', 'true'); } catch {}
                                    }}
                                />
                            )}
                        </div>
                    }
                />
                <Route
                    path="/library"
                    element={
                        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
                            <h1 className="text-3xl font-bold font-display text-text-primary mb-6">Library</h1>
                            <BoardgameCollectionView
                                backendService={backendService}
                                pocketbaseURL={pocketbaseURL}
                                onItemsLoaded={setCollectionItems}
                                externalSelect={searchSelection}
                                pageMode="library"
                                onSearchOpen={() => setSearchOpen(true)}
                            />
                        </div>
                    }
                />
                <Route
                    path="/lists"
                    element={
                        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
                            <h1 className="text-3xl font-bold font-display text-text-primary mb-6">Lists</h1>
                            <ListsOverview backendService={backendService} />
                        </div>
                    }
                />
                <Route
                    path="/lists/:listId"
                    element={
                        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
                            <ListDetailPage backendService={backendService} pocketbaseURL={pocketbaseURL} />
                        </div>
                    }
                />
                <Route
                    path="/pods"
                    element={
                        <>
                            <div className="h-0 overflow-hidden">
                                <BoardgameCollectionView
                                    backendService={backendService}
                                    pocketbaseURL={pocketbaseURL}
                                    onItemsLoaded={setCollectionItems}
                                    externalSelect={searchSelection}
                                    pageMode="pods"
                                />
                            </div>
                            <PodsPage
                                items={collectionItems}
                                collectionType="boardgames"
                                getItemImage={(item) => {
                                    const pbImage = item.thumbImage;
                                    if (pbImage) return `${pocketbaseURL}/api/files/boardgames/${item.id}/${pbImage}`;
                                    return item.imageURL || null;
                                }}
                                getItemSubtitle={(item) => item.yearPublished ? String(item.yearPublished) : null}
                                onItemClick={(item) => setSearchSelection({ ...item, _ts: Date.now() })}
                            />
                        </>
                    }
                />
                <Route
                    path="/settings"
                    element={<SettingsPage />}
                />
                <Route
                    path="/notifications"
                    element={<NotificationsPage notifications={notifications} unreadCount={unreadCount} markAllRead={markAllRead} loading={notifLoading} />}
                />
            </Routes>

            <SearchOverlay
                open={searchOpen}
                onClose={() => { setSearchOpen(false); setSearchInitialQuery(''); setSearchCameraOnOpen(false); }}
                initialQuery={searchInitialQuery}
                openCameraOnMount={searchCameraOnOpen}
                appType="boardgames"
                items={collectionItems}
                onSelect={handleSearchSelect}
                placeholder="Search board games..."
                onExternalSearch={handleExternalSearch}
                onExternalAction={handleExternalAction}
                externalSearchLabel="Search BoardGameGeek"
                externalItemKey={(item) => String(item.bggId)}
                renderPostAdd={(addedItem, { onDone }) => (
                    <PostAddPrompt addedItem={addedItem} itemType="boardgame" activityType="played" onDone={onDone} backendService={backendService} activeView={activeView} />
                )}
                renderExternalResult={(item, { onAction, isLoading, isDone }) => (
                    <SearchResultRow
                        image={item.thumbnail ? (
                            <img src={item.thumbnail} alt={item.name} className="w-10 h-14 object-cover rounded flex-shrink-0" />
                        ) : (
                            <div className="w-10 h-14 bg-bg-tertiary rounded flex-shrink-0 flex items-center justify-center">
                                <span className="text-lg">🎲</span>
                            </div>
                        )}
                        itemId={String(item.bggId)}
                        itemTitle={item.name}
                        itemImageURL={item.thumbnail || ''}
                        backendService={backendService}
                        activeView={activeView}
                        onAction={onAction}
                        isLoading={isLoading}
                        isDone={isDone}
                    >
                        <p className="text-sm font-medium text-text-primary truncate font-body">{item.name}</p>
                        {item.yearPublished && (
                            <p className="text-xs text-text-secondary font-body">{item.yearPublished}</p>
                        )}
                    </SearchResultRow>
                )}
            />
        </div>
    );
}

function App() {
    return (
        <UsersProvider pocketbaseUrl={import.meta.env.VITE_POCKETBASE_URL}>
            <UserViewProvider>
                <AuthGate background={<TrendingSignInBackground />}>
                    <Router>
                        <AppContent />
                    </Router>
                </AuthGate>
            </UserViewProvider>
        </UsersProvider>
    );
}

export default App;
